import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    completeInspection, skipInspection, finishInspectionAction, updateInspectionActionDue, saveInspectionDraft, InspectionDetail,
} from '../../services/inspectionsApi';
import InspectionForm, { Answers, fillContactFields } from './InspectionForm';
import { useAuth } from '../../context/AuthContext';
import DeviationChoice, { DeviationState, emptyDeviation } from './DeviationChoice';
import ActionPanel from './ActionPanel';
import { evaluateLive } from './evaluate';
import { btn, btnSmall, StatusBadge } from './common';
import { ui } from '../../styles/shared';
import { fmtDate, fmtDateTime } from '../../utils/format';

/**
 * Panelen för en öppen kontroll: fylls i om den är att göra, annars visas den.
 * Visas ovanför flikarna och används från både "Att göra" och "Historik".
 * Föräldern byter key när en ny kontroll öppnas så att fälten nollställs.
 */
interface Props {
    inspection: InspectionDetail;
    /** Kontrollen har sparats eller åtgärden klarmarkerats; föräldern får det nya svaret. */
    onChange: (d: InspectionDetail) => void;
    onClose: () => void;
    /** Laddar om listor och räknare i föräldern. */
    reload: () => Promise<void>;
}

const ActiveInspection: React.FC<Props> = ({ inspection: active, onChange, onClose, reload }) => {
    const { user } = useAuth();
    // Kontaktfält får den inloggades namn direkt när kontrollen öppnas.
    const [answers, setAnswers] = useState<Answers>(() => fillContactFields(active.fields, active.answers ?? {}, user?.username ?? ''));
    const [deviation, setDeviation] = useState<DeviationState>(emptyDeviation());
    const [note, setNote] = useState(active.note ?? '');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [deviationKeys, setDeviationKeys] = useState<string[]>([]);
    const [savingDraft, setSavingDraft] = useState(false);
    const [draftSaved, setDraftSaved] = useState(false);

    const isOpen = active.status === 'pending' || active.status === 'overdue';
    // Avvikelser räknas ut direkt medan man fyller i; servern räknar om vid sparning.
    const liveKeys = useMemo(() => (isOpen ? evaluateLive(active.fields, answers).deviationKeys : []), [active.fields, isOpen, answers]);
    const shownKeys = isOpen ? liveKeys : deviationKeys;

    // Hämta avvikelselistan från ett 400-svar så rätt fält markeras.
    useEffect(() => {
        const id = axios.interceptors.response.use(r => r, err => {
            if (err?.response?.status === 400 && Array.isArray(err.response.data?.deviations) && err.response.data.deviations.length) {
                setDeviationKeys(err.response.data.deviations.map((d: any) => d.key));
            }
            return Promise.reject(err);
        });
        return () => axios.interceptors.response.eject(id);
    }, []);

    const submit = async (noNeed = false) => {
        setSaving(true); setFormError(null);
        try {
            const res = await completeInspection(active.id, {
                answers, note, no_need: noNeed,
                deviation_mode: deviation.mode,
                deviation_note: deviation.deviationNote,
                action_note: deviation.actionNote,
                action_due_date: deviation.actionDue,
            });
            onChange(res);
            setDeviationKeys((res.deviations ?? []).map(d => d.key));
            await reload();
        } catch (e: any) {
            const data = (e as any)?.deviations;
            setFormError(e.message);
            if (Array.isArray(data)) setDeviationKeys(data.map((d: any) => d.key));
        } finally { setSaving(false); }
    };

    /** Sparar det som är ifyllt så långt; kontrollen ligger kvar som påbörjad. */
    const saveDraft = async () => {
        setSavingDraft(true); setFormError(null); setDraftSaved(false);
        try {
            const res = await saveInspectionDraft(active.id, { answers, note });
            onChange(res);
            setDraftSaved(true);
            await reload();
        } catch (e: any) { setFormError(e.message); }
        finally { setSavingDraft(false); }
    };

    const finishAction = async (noteText: string) => {
        const res = await finishInspectionAction(active.id, noteText);
        onChange(res);
        await reload();
    };

    const changeActionDue = async (date: string) => {
        const res = await updateInspectionActionDue(active.id, date);
        onChange(res);
        await reload();
    };

    const skip = async () => {
        const reason = window.prompt('Varför hoppas kontrollen över?');
        if (!reason?.trim()) return;
        try { await skipInspection(active.id, reason.trim()); onClose(); await reload(); }
        catch (e: any) { setFormError(e.message); }
    };

    return (
        <div style={{ ...ui.card, borderColor: '#0f3460', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <div>
                    <h2 style={{ margin: '0 0 4px' }}>{active.schedule_name ?? active.template_name}</h2>
                    <div style={{ fontSize: 13, color: '#666' }}>
                        {active.template_name}{active.zone_name ? ` · ${active.zone_name}` : ''} · senast {fmtDate(active.due_date)}
                        {active.status === 'done' && <> · {active.no_need ? 'utfört, inget behov' : 'gjord'} {fmtDateTime(active.completed_at)} av {active.completed_by_name ?? 'okänd'}</>}
                        {active.escalation_sent_at && <> · <span style={{ color: '#b02a37' }}>eskalerad {fmtDateTime(active.escalation_sent_at)}</span></>}
                        {active.has_draft && isOpen && (active.started_by_name
                            ? <> · påbörjad av <b>{active.started_by_name}</b>{active.started_at ? ` ${fmtDateTime(active.started_at)}` : ''}</>
                            : <> · påbörjad, fortsätt där du slutade</>)}
                        {' '}<StatusBadge i={active} />
                    </div>
                </div>
                <button onClick={onClose} style={btnSmall('#6c757d')}>Stäng</button>
            </div>

            <div style={{ marginTop: 14 }}>
                <InspectionForm
                    fields={active.fields}
                    answers={answers}
                    prefill={active.prefill}
                    readOnly={!isOpen}
                    onChange={(k, v) => setAnswers(prev => ({ ...prev, [k]: v }))}
                    deviationKeys={shownKeys}
                />
            </div>

            {isOpen ? (
                <>
                    {shownKeys.length > 0 && <DeviationChoice value={deviation} onChange={setDeviation} />}
                    <div style={{ marginTop: 10 }}>
                        <label style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Kommentar (valfritt)</label>
                        <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: 6, padding: 9, fontSize: 15, fontFamily: 'inherit' }} />
                    </div>
                    {formError && <div style={{ background: '#f8d7da', color: '#721c24', padding: 10, borderRadius: 4, marginTop: 10 }}>{formError}</div>}
                    <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                        <button onClick={() => submit(false)} disabled={saving || savingDraft} style={btn('#28a745')}>{saving ? 'Sparar…' : 'Spara kontrollen'}</button>
                        <button onClick={() => submit(true)} disabled={saving || savingDraft} style={btn('#20c997')}
                            title="Jag var där och tittade, inget behövde göras. Inga fält krävs.">
                            Utfört, inget behov
                        </button>
                        <button onClick={saveDraft} disabled={saving || savingDraft} style={btn('#e8f0fe', '#1a4aa6')} title="Sparar det som är ifyllt. Kontrollen räknas inte som gjord förrän du trycker Spara kontrollen.">
                            {savingDraft ? 'Sparar…' : 'Spara och fortsätt senare'}
                        </button>
                        <button onClick={skip} style={btn('#6c757d')}>Hoppa över</button>
                        {draftSaved && <span style={{ fontSize: 13, color: '#28a745', alignSelf: 'center' }}>Utkastet är sparat. Kontrollen ligger kvar under Att göra.</span>}
                        <span style={{ fontSize: 12, color: '#888', alignSelf: 'center' }}>Länk utan inloggning: <code>/inspection/{active.token}</code></span>
                    </div>
                </>
            ) : (
                <div style={{ marginTop: 10, fontSize: 14 }}>
                    {active.has_deviation && active.deviation_note && <div style={{ background: '#fff3cd', padding: 10, borderRadius: 6, marginBottom: 8 }}><b>Avvikelse, åtgärdat direkt:</b> {active.deviation_note}</div>}
                    <ActionPanel inspection={active} onDone={noteText => finishAction(noteText)} onChangeDue={changeActionDue} />
                    {active.note && <div style={{ marginTop: 8 }}><b>Kommentar:</b> {active.note}</div>}
                </div>
            )}
        </div>
    );
};

export default ActiveInspection;
